/**
 * Local and regional-sports channels for a ZIP + DMA, copied from production's
 * `LocalRsnChnlService` and the channels-service locals overlay. Each beam
 * response (not the overlay's) reports a form response through
 * `services/analytics`; a network error, or a body that does not parse, reports
 * nothing.
 */

import { fireFormResponse } from "../analytics";
import { DEFAULT_CHANNEL_LOOKUP_PATH } from "./county-lookup";

export interface LocalRsnOptions {
  /** Ask the stream (`ottchannels`) endpoint. Needs a Nielsen DMA. */
  stream?: boolean;
  /** Ask the satellite (`dtvchannels`) endpoint. Needs a DTV DMA. */
  satellite?: boolean;
  /** Empty the local list when false. Defaults to true. */
  local?: boolean;
  /** Empty the regional-sports list when false. Defaults to true. */
  rsn?: boolean;
}

export interface LocalRsnParams {
  zip: string;
  dma?: string;
  nielsenDma?: string;
}

export interface LocalRsnConfig {
  /** Base path of the channel lookup service, trailing slash included. */
  channelLookupPath?: string;
  /** Prefix for `/api/local-channels` — the app's base path, "" when it has none. */
  localChannelsBasePath?: string;
}

/** One station as the lookup service sends it. Other fields pass through untouched. */
export interface RawStation {
  callSign?: string;
  network?: string;
  channelNumber?: number;
  logo?: string;
  packageNames?: unknown;
  [field: string]: unknown;
}

/** A station dressed in the shape consumers were built against. */
export interface Station extends RawStation {
  IsActive: boolean;
  isStream: boolean;
  isSatellite: boolean;
  TYPE: string;
  callLetter?: string;
  categoryId: string;
  categoryIdSystem: string;
  chnlNum?: number;
  id?: number;
  label?: string;
  labelSystem?: string;
  logo: string;
}

export interface LocalRsnResult {
  localChannels: Station[];
  rsnChannels: Station[];
}

/** An error carrying the failed response's status, for the host to report. */
export class ChannelsRequestError extends Error {
  status?: number;

  statusText?: string;

  constructor(message: string, status?: number, statusText?: string) {
    super(message);
    this.status = status;
    this.statusText = statusText;
  }
}

const EVENT_CODE = "VS_LocalRegionalChannelSearch_Submit";
const LINK_NAME = "Search";
const LINK_POSITION = "Body";
const ERROR_TYPE_SYSTEM = "Failure_System";
const ERROR_TYPE_DOWNTIME = "Planned_Downtime";
const ERROR_TYPE_SUCCESS = "Success_Admit";
const MSG_500 = "We're sorry, we encountered an error on our end.";
const MSG_404 = "We're sorry, please try again at a later time.";
const MSG_SUCCESS = "SUCCESS";

/** How long the channels-service overlay may take before beam's locals stand. */
export const SERVICE_LOCALS_TIMEOUT_MS = 1500;

type ProductKind = "stream" | "satellite";

/**
 * ⚠ `logo` is guarded here where production calls `startsWith` unguarded and
 * throws on a missing logo — a station without art should still list.
 */
export function buildChannelObject(channel: RawStation, type: "local" | "rsn", product: ProductKind): Station {
  const logoPath = `/dtvassets/global/channel-icons${type === "rsn" ? "/rsn-channels" : "/local-channels"}`;
  const channelType = type.toUpperCase();
  const logo: string = typeof channel?.logo === "string" ? channel.logo : "";
  return {
    ...channel,
    IsActive: true,
    isStream: product === "stream",
    isSatellite: product === "satellite",
    TYPE: channelType,
    callLetter: channel?.callSign,
    categoryId: channelType,
    categoryIdSystem: channelType,
    chnlNum: channel?.channelNumber,
    id: channel?.channelNumber,
    label: channel?.network,
    labelSystem: channel?.network,
    logo: `${logoPath}${logo.startsWith("/") ? logo : `/${logo}`}`,
    packageNames: channel?.packageNames
  };
}

/**
 * Dedupes by "callSign network channelNumber". A repeat replaces the earlier
 * entry and marks it available on both products; insertion order is kept.
 */
export function mergeChannels(channels: Station[]): Station[] {
  const unique = new Map<string, Station>();
  for (const channel of channels || []) {
    const key = `${channel?.callSign} ${channel?.network} ${channel?.channelNumber}`;
    unique.set(key, unique.has(key) ? {
      ...channel,
      isSatellite: true,
      isStream: true
    } : channel);
  }
  return Array.from(unique.values());
}

function reportResponse(
  fields: { statusCode: number; errorType: string; statusMessage: string; successFlag: string }
) {
  fireFormResponse({
    ...fields,
    eventCode: EVENT_CODE,
    linkName: LINK_NAME,
    linkPosition: LINK_POSITION
  });
}

/**
 * One channels request. Reports once per HTTP response: a failure status
 * reports and rejects; a success reports AFTER the body parses, so unparseable
 * JSON reports nothing. A network error reports nothing either.
 *
 * ⚠ The 503 branch reports the 500 message, and any other failing status
 * reports empty strings — production's own payloads, kept so dashboards keyed
 * on them keep counting.
 */
async function requestChannels(url: string): Promise<{ localChannels?: RawStation[]; rsnChannels?: RawStation[] }> {
  const response = await fetch(url);
  if (!response.ok) {
    const { status } = response;
    reportResponse({
      statusCode: status,
      errorType: status === 500 || status === 503 ? ERROR_TYPE_SYSTEM : status === 404 ? ERROR_TYPE_DOWNTIME : "",
      statusMessage: status === 500 || status === 503 ? MSG_500 : status === 404 ? MSG_404 : "",
      successFlag: "0"
    });
    throw new ChannelsRequestError("Channels request failed", status, response.statusText);
  }
  const data = await response.json();
  reportResponse({
    errorType: ERROR_TYPE_SUCCESS,
    statusCode: response.status,
    statusMessage: MSG_SUCCESS,
    successFlag: "1"
  });
  return {
    localChannels: data?.[0]?.localChannels,
    rsnChannels: data?.[0]?.rsnChannels
  };
}

function build(list: RawStation[] | undefined, type: "local" | "rsn", product: ProductKind, wanted: boolean) {
  if (!wanted || !list || list.length === 0) {
    return [];
  }
  return list.map(channel => buildChannelObject(channel, type, product));
}

/** Beam's answer, from the stream endpoint, the satellite one, or both merged. */
async function fetchBeam(
  { zip, dma, nielsenDma }: LocalRsnParams,
  options: LocalRsnOptions,
  base: string
): Promise<LocalRsnResult> {
  const stream = options.stream === true;
  const satellite = options.satellite === true;
  const wantLocal = options.local !== false;
  const wantRsn = options.rsn !== false;

  if (stream && nielsenDma && satellite && dma) {
    const results = await Promise.allSettled([
      requestChannels(`${base}ottchannels/zipcode/${zip}/${nielsenDma}`),
      requestChannels(`${base}dtvchannels/zipcode/${zip}/${dma}`)
    ]);
    const failed = results.filter(result => result.status === "rejected") as PromiseRejectedResult[];
    if (failed.length > 0) {
      /* No status, no status text: production reads them off the fulfilled results,
         which a failed pair does not have, so they are always undefined there. */
      throw new ChannelsRequestError("Channels request failed");
    }
    const [streamData, satelliteData] = results.map(result => (result as PromiseFulfilledResult<{ localChannels?: RawStation[]; rsnChannels?: RawStation[] }>).value);
    return {
      localChannels: mergeChannels([
        ...build(streamData.localChannels, "local", "stream", wantLocal),
        ...build(satelliteData.localChannels, "local", "satellite", wantLocal)
      ]),
      rsnChannels: mergeChannels([
        ...build(streamData.rsnChannels, "rsn", "stream", wantRsn),
        ...build(satelliteData.rsnChannels, "rsn", "satellite", wantRsn)
      ])
    };
  }

  /* Stream was asked for but there is no Nielsen DMA: the satellite endpoint is
     used, with the DTV DMA in the path. */
  const url = stream && nielsenDma ?
    `${base}ottchannels/zipcode/${zip}/${nielsenDma}` :
    `${base}dtvchannels/zipcode/${zip}/${dma}`;
  const product: ProductKind = stream ? "stream" : "satellite";
  const data = await requestChannels(url);
  return {
    localChannels: build(data.localChannels, "local", product, wantLocal),
    rsnChannels: build(data.rsnChannels, "rsn", product, wantRsn)
  };
}

/** The `<html>` flag the server writes when channels-service locals are on, read at call time. */
export function isServiceLocalsOn(): boolean {
  return typeof document !== "undefined" &&
    document.documentElement?.getAttribute?.("data-channelservice-locals") === "true";
}

/**
 * Stations from channels-service for one market, or `null` for "use beam":
 * the flag is off, the market is not in the feed, the call failed or timed out,
 * or the body had no list. A fetch for a given platform and DMA never throws.
 */
export async function fetchServiceLocals(
  dma: string | undefined,
  platform: "ott" | "satellite",
  basePath = ""
): Promise<RawStation[] | null> {
  if (!isServiceLocalsOn() || !dma) {
    return null;
  }
  const url = `${basePath}/api/local-channels?dma=${encodeURIComponent(dma)}&platform=${encodeURIComponent(platform)}`;
  const controller = typeof AbortController === "undefined" ? undefined : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), SERVICE_LOCALS_TIMEOUT_MS) : undefined;
  try {
    const response = await fetch(url, { signal: controller?.signal });
    if (response?.status === 404 || !response?.ok) {
      return null;
    }
    const body = await response.json();
    return Array.isArray(body?.localChannels) ? body.localChannels : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** A service station dressed like a beam one; its own resolved logo is kept. */
function buildServiceStation(station: RawStation, product: ProductKind): Station {
  const file = typeof station?.logo === "string" ? station.logo.split("local-channels/")[1] : undefined;
  const built = buildChannelObject({
    ...station,
    logo: file ?? ""
  }, "local", product);
  return file ? built : {
    ...built,
    logo: station?.logo
  };
}

async function serviceLocalsFor(
  nielsenDma: string | undefined,
  options: LocalRsnOptions,
  basePath: string
): Promise<Station[] | null> {
  if (options.local === false) {
    return null;
  }
  const platforms: ["ott" | "satellite", ProductKind][] = options.stream && options.satellite ?
    [["ott", "stream"], ["satellite", "satellite"]] :
    [options.satellite ? ["satellite", "satellite"] : ["ott", "stream"]];
  const perPlatform = await Promise.all(platforms.map(async ([platform, product]) => (
    (await fetchServiceLocals(nielsenDma, platform, basePath))?.map(station => buildServiceStation(station, product)) ?? []
  )));
  const stations = perPlatform.flat();
  if (stations.length === 0) {
    return null;
  }
  return perPlatform.length > 1 ? mergeChannels(stations) : stations;
}

/**
 * Locals and regional channels for a ZIP + DMA. Rejects on failure.
 *
 * With channels-service on, its stations replace beam's locals whenever it
 * returns any; regional channels always come from beam. If beam fails but the
 * service has stations, those are returned with no regional channels.
 */
export async function fetchLocalRsnChannels(
  params: LocalRsnParams,
  options: LocalRsnOptions = { satellite: true },
  { channelLookupPath = DEFAULT_CHANNEL_LOOKUP_PATH, localChannelsBasePath = "" }: LocalRsnConfig = {}
): Promise<LocalRsnResult> {
  const service = serviceLocalsFor(params.nielsenDma, options, localChannelsBasePath).catch(() => null);
  let beam: LocalRsnResult;
  try {
    beam = await fetchBeam(params, options, channelLookupPath);
  } catch (error) {
    const stations = await service;
    if (stations && stations.length > 0) {
      return {
        localChannels: stations,
        rsnChannels: []
      };
    }
    throw error;
  }
  const stations = await service;
  return stations && stations.length > 0 ? {
    ...beam,
    localChannels: stations
  } : beam;
}
